/**
 * Formatting for the two kinds of date a payment carries, which are not the
 * same kind of thing and must not share a formatter.
 *
 * A pay period is date-only cohort data persisted at UTC midnight, so it is
 * pinned to UTC: formatting it in the viewer's zone shifts the day west of UTC
 * and prints a week that ends the day before it does. A paid date is a true
 * instant, and is read as a day in the payout zone for the reasons on
 * `formatInstantDay`.
 *
 * Day, month and weekday are read as separate parts and joined here rather
 * than taken from one locale-ordered format, because no single locale gives
 * "5 Sep": `en-GB` orders it right and abbreviates September as "Sept", and
 * `en-US` abbreviates it right and orders it "Sep 5".
 */

import { TERAC_PAYOUT_TIMEZONE } from "@repo/common";

export {
  formatInstantDay,
  formatInstantDayWithYear,
  formatRateEndsAt,
} from "@repo/common/payout-instant";

const UTC_DAY = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  timeZone: "UTC",
});
const UTC_MONTH = new Intl.DateTimeFormat("en-US", {
  month: "short",
  timeZone: "UTC",
});
const UTC_WEEKDAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  weekday: "short",
});
const UTC_YEAR = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  year: "numeric",
});
const DAY_MS = 86_400_000;
/** A pay period this long or shorter is described as a week. */
const WEEK_SPAN_MS = 8 * DAY_MS;

function ordinal(day: number): string {
  const remainderTen = day % 10;
  const remainderHundred = day % 100;
  if (remainderTen === 1 && remainderHundred !== 11) {
    return `${day}st`;
  }
  if (remainderTen === 2 && remainderHundred !== 12) {
    return `${day}nd`;
  }
  if (remainderTen === 3 && remainderHundred !== 13) {
    return `${day}rd`;
  }
  return `${day}th`;
}

function utcParts(value: Date | string) {
  const date = new Date(value);
  return {
    date,
    day: ordinal(Number(UTC_DAY.format(date))),
    month: UTC_MONTH.format(date),
    year: UTC_YEAR.format(date),
  };
}

/**
 * The row title: "Week of 18th – 24th Aug" when the period is a week, the
 * spanned dates otherwise. The year appears only when it is not the current
 * one, because on the common case it is noise and on an old row its absence is
 * the ambiguity.
 */
export function formatPeriodLabel(
  start: Date | string | null,
  end: Date | string | null,
  now: Date = new Date(),
): string | null {
  const thisYear = UTC_YEAR.format(now);
  if (start && end) {
    const from = utcParts(start);
    const to = utcParts(end);
    const suffix = to.year === thisYear ? "" : ` ${to.year}`;
    const span = to.date.getTime() - from.date.getTime();
    const range =
      from.month === to.month
        ? `${from.day} – ${to.day} ${to.month}${suffix}`
        : `${from.day} ${from.month} – ${to.day} ${to.month}${suffix}`;
    return span >= 0 && span <= WEEK_SPAN_MS ? `Week of ${range}` : range;
  }
  if (start) {
    const from = utcParts(start);
    const suffix = from.year === thisYear ? "" : ` ${from.year}`;
    return `From ${from.day} ${from.month}${suffix}`;
  }
  if (end) {
    const to = utcParts(end);
    const suffix = to.year === thisYear ? "" : ` ${to.year}`;
    return `Until ${to.day} ${to.month}${suffix}`;
  }
  return null;
}

const PAYOUT_ZONE_DAY = new Intl.DateTimeFormat("en-CA", {
  day: "2-digit",
  month: "2-digit",
  timeZone: TERAC_PAYOUT_TIMEZONE,
  year: "numeric",
});

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "Fri 5 Sep", or "Today" on the day itself.
 *
 * The API deliberately hands over a `"YYYY-MM-DD"` string rather than an
 * instant, so this must not put it through `new Date(value)` and a local
 * formatter: that parses as UTC midnight and renders the previous day for
 * every viewer west of UTC, which is most of them. The parts are read straight
 * out of the string and formatted in UTC, and "today" is decided in the payout
 * zone the date was computed in.
 */
export function formatNextPayoutDate(
  isoDate: string,
  now: Date = new Date(),
): string | null {
  const match = ISO_DATE.exec(isoDate);
  const [, year, month, day] = match ?? [];
  if (!year || !month || !day) {
    return null;
  }
  if (isoDate === PAYOUT_ZONE_DAY.format(now)) {
    return "Today";
  }
  const asUtc = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  );
  return `${UTC_WEEKDAY.format(asUtc)} ${UTC_DAY.format(asUtc)} ${UTC_MONTH.format(asUtc)}`;
}
